import { createElement, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { collageFrame, type CollagePhoto } from './collage-renderer.js';

/** Сохраняем точную геометрию шаблонизатора; DOM и события холста принадлежат React. */
export function CollageCanvas({
  layout,
  ratio,
  gap,
  background,
  caption,
  photos,
  onSlot,
  onSwap,
}: {
  layout: string;
  ratio: string;
  gap: string;
  background: string;
  caption: string;
  photos: Array<CollagePhoto | null>;
  onSlot?(index: number): void;
  onSwap?(from: number, to: number): void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const gesture = useRef<{
    from: number;
    pointer: number;
    x: number;
    y: number;
    active: boolean;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const suppressClick = useRef(false);
  const [ghost, setGhost] = useState<{ url: string; x: number; y: number } | null>(null);
  useEffect(() => {
    const element = root.current;
    const touch = (event: TouchEvent) => {
      if (gesture.current?.active) event.preventDefault();
    };
    element?.addEventListener('touchmove', touch, { passive: false });
    return () => {
      element?.removeEventListener('touchmove', touch);
      if (gesture.current) clearTimeout(gesture.current.timer);
    };
  }, []);
  const rendered = useMemo(() => {
    const frame = collageFrame({ layout, ratio, gap, background, caption, files: photos });
    const document = new DOMParser().parseFromString(frame.html, 'text/html');
    const convert = (node: Node, key: number): ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent;
      if (!(node instanceof Element) || node.tagName === 'INPUT') return null;
      const props: Record<string, unknown> = { key };
      for (const attribute of Array.from(node.attributes)) {
        if (attribute.name === 'class') props.className = attribute.value;
        else if (attribute.name === 'style' && node instanceof HTMLElement) {
          const style: Record<string, string> = {};
          for (const name of Array.from(node.style))
            style[name.startsWith('--') ? name : name.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase())] =
              node.style.getPropertyValue(name);
          props.style = style as CSSProperties;
        } else if (
          ['src', 'alt', 'viewBox', 'viewbox', 'fill', 'stroke', 'd', 'draggable'].includes(attribute.name) ||
          attribute.name.startsWith('data-') ||
          attribute.name.startsWith('aria-')
        )
          props[attribute.name === 'viewbox' ? 'viewBox' : attribute.name] = attribute.value;
        else if (attribute.name === 'stroke-width') props.strokeWidth = attribute.value;
      }
      const slot = node.getAttribute('data-cl-slot');
      const interactive = slot !== null && !!(onSlot || onSwap);
      const tag = interactive ? 'button' : node.tagName === 'LABEL' ? 'span' : node.tagName.toLowerCase();
      if (interactive) {
        const index = Number(slot);
        props.type = 'button';
        props['aria-label'] = `Фото ${index + 1}`;
        props.disabled = !onSlot && !onSwap;
        props.draggable = !!onSwap && !!photos[index];
        props.onClick = () => {
          if (suppressClick.current) {
            suppressClick.current = false;
            return;
          }
          onSlot?.(index);
        };
        props.onPointerDown = (event: React.PointerEvent) => {
          if (event.pointerType !== 'touch' || !onSwap || !photos[index]) return;
          suppressClick.current = false;
          const timer = setTimeout(() => {
            if (!gesture.current) return;
            gesture.current.active = true;
            suppressClick.current = true;
            root.current?.setPointerCapture(event.pointerId);
            setGhost({ url: photos[index]!.url, x: event.clientX, y: event.clientY });
          }, 400);
          gesture.current = { from: index, pointer: event.pointerId, x: event.clientX, y: event.clientY, active: false, timer };
        };
        props.onDragStart = (event: React.DragEvent) => event.dataTransfer.setData('application/x-steos-photo-slot', String(index));
        props.onDragOver = (event: React.DragEvent) => {
          if (onSwap) event.preventDefault();
        };
        props.onDrop = (event: React.DragEvent) => {
          event.preventDefault();
          const raw = event.dataTransfer.getData('application/x-steos-photo-slot');
          if (/^\d+$/.test(raw)) onSwap?.(Number(raw), index);
        };
      }
      const children = Array.from(node.childNodes).map((child, index) => convert(child, index));
      return createElement(tag, props, ...children);
    };
    return { ratio: frame.ratio, content: Array.from(document.body.childNodes).map((node, index) => convert(node, index)) };
  }, [layout, ratio, gap, background, caption, photos, onSlot, onSwap]);
  return (
    <div
      ref={root}
      className="studio-collage-canvas"
      aria-hidden={!onSlot && !onSwap}
      style={{ aspectRatio: rendered.ratio }}
      onPointerMove={event => {
        const active = gesture.current;
        if (!active) return;
        if (!active.active && Math.hypot(event.clientX - active.x, event.clientY - active.y) > 10) {
          clearTimeout(active.timer);
          gesture.current = null;
        } else if (active.active) {
          event.preventDefault();
          setGhost({ url: photos[active.from]!.url, x: event.clientX, y: event.clientY });
        }
      }}
      onPointerUp={event => {
        const active = gesture.current;
        if (!active) return;
        clearTimeout(active.timer);
        if (active.active) {
          const target = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-cl-slot]');
          if (target && root.current?.contains(target)) onSwap?.(active.from, Number(target.getAttribute('data-cl-slot')));
          if (root.current?.hasPointerCapture(active.pointer)) root.current.releasePointerCapture(active.pointer);
        }
        gesture.current = null;
        setGhost(null);
      }}
      onPointerCancel={() => {
        if (gesture.current) clearTimeout(gesture.current.timer);
        gesture.current = null;
        setGhost(null);
      }}
    >
      {rendered.content}
      {ghost && (
        <span className="cl-ghost" aria-hidden="true" style={{ transform: `translate(${ghost.x - 34}px,${ghost.y - 34}px)` }}>
          <img src={ghost.url} alt="" />
        </span>
      )}
    </div>
  );
}
